import {
  CreateGroupSchema,
  GetUngroupedBodySchema,
  GroupCountSchema,
  GroupSchema,
  GroupVariationCountSchema,
  MergeGroupSchema,
  NameFrequencySchema,
  SearchUngroupedBodySchema,
  UpdateGroupSchema,
  type Group,
  type GroupCount,
  type GroupVariationCount,
  type NameFrequency,
} from '@pin/schemas/admin';
import { ErrorResponseSchema } from '@pin/schemas/shared';
import { collectionData } from 'rxfire/firestore';
import { firstValueFrom } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { map, shareReplay, timeout } from 'rxjs/operators';
import * as z from 'zod';
import { withIdToken } from '../firebase/auth';
import {
  groupsCol,
  variationsColGroup,
} from '../firebase/firestore/collections';
import { adminApiUrl } from '../utils/context';
import { HttpError, retryWithBackoff } from '../utils/rxjs.http';

export const allGroups$ = collectionData(groupsCol).pipe(
  map((g) => g?.toSorted((a, b) => b.createdAt - a.createdAt)),
  shareReplay({ bufferSize: 1, refCount: true })
);
export const allVariations$ = collectionData(variationsColGroup).pipe(
  map((v) => v?.toSorted((a, b) => b.createdAt - a.createdAt)),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const createGroup = (
  data: z.input<typeof CreateGroupSchema>
): Promise<Group> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(CreateGroupSchema.parse(data)),
        selector: async (res) => {
          if (!res.ok) {
            throw new HttpError(res);
          }
          return GroupSchema.parse(await res.json());
        },
      }).pipe(timeout(10_000))
    )
  );

export const updateGroup = (
  groupId: string,
  data: z.input<typeof UpdateGroupSchema>
): Promise<void> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/groups/${groupId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(UpdateGroupSchema.parse(data)),
        selector: async (res) => {
          if ([404].includes(res.status)) {
            throw new Error(
              ErrorResponseSchema.parse(await res.json()).message
            );
          }
          if (!res.ok) {
            throw new HttpError(res);
          }
        },
      }).pipe(timeout(30_000))
    )
  );

export const deleteGroup = (groupId: string): Promise<void> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/groups/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        selector: async (res) => {
          if ([404].includes(res.status)) {
            throw new Error(
              ErrorResponseSchema.parse(await res.json()).message
            );
          }
          if (!res.ok) {
            throw new HttpError(res);
          }
        },
      }).pipe(timeout(30_000))
    )
  );

export const mergeGroups = (
  data: z.input<typeof MergeGroupSchema>
): Promise<void> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/groups/merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(MergeGroupSchema.parse(data)),
        selector: async (res) => {
          if ([404].includes(res.status)) {
            throw new Error(
              ErrorResponseSchema.parse(await res.json()).message
            );
          }
          if (!res.ok) {
            throw new HttpError(res);
          }
        },
      }).pipe(timeout(30_000))
    )
  );

export const fetchGroupCounts = (
  participantId?: string
): Promise<GroupCount[]> => {
  const searchParams = new URLSearchParams({});
  if (participantId != null) {
    searchParams.append('participantId', participantId);
  }
  return firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/groups/counts?${searchParams}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        selector: async (res) => {
          if (!res.ok) {
            throw new HttpError(res);
          }
          return z.array(GroupCountSchema).parse(await res.json());
        },
      }).pipe(timeout(10_000), retryWithBackoff(2))
    )
  );
};

export const fetchGroupVariationCounts = (
  groupId: string
): Promise<GroupVariationCount[]> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/groups/${groupId}/counts`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        selector: async (res) => {
          if (!res.ok) {
            throw new HttpError(res);
          }
          return z.array(GroupVariationCountSchema).parse(await res.json());
        },
      }).pipe(timeout(10_000), retryWithBackoff(2))
    )
  );

export const fetchUngrouped = (
  body: z.input<typeof GetUngroupedBodySchema>
): Promise<NameFrequency[]> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/names/ungrouped`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(GetUngroupedBodySchema.parse(body)),
        selector: async (res) => {
          if (!res.ok) {
            throw new HttpError(res);
          }
          return z.array(NameFrequencySchema).parse(await res.json());
        },
      }).pipe(timeout(10_000), retryWithBackoff(2))
    )
  );

export const searchUngrouped = (
  body: z.input<typeof SearchUngroupedBodySchema>
): Promise<NameFrequency[]> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/names/ungrouped/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(SearchUngroupedBodySchema.parse(body)),
        selector: async (res) => {
          if (!res.ok) {
            throw new HttpError(res);
          }
          return z.array(NameFrequencySchema).parse(await res.json());
        },
      }).pipe(timeout(10_000), retryWithBackoff(2))
    )
  );
